import React from "react";
import objectHash from "object-hash";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
const PrefetchContext = React.createContext(false);
class PrefetchStore {
    constructor() {
        if (typeof window !== "undefined") {
            const foundById = document.getElementById('qStore');
            if (foundById) {
                const parsed = JSON.parse(foundById.textContent);
                this.fulfilledDependents = parsed.fulfilledDependents;
                this.fulfilledQueries = parsed.fulfilledQueries;
                this.queries = parsed.queries;
            }
        }
    }
    pathname = "";
    queries = {};
    fulfilledQueries = {};
    fulfilledDependents = {};
    modelTrack = {};
    queue(props, modelTrack) {
        this.queries[objectHash(props)] = props;
        this.modelTrack = modelTrack;
        if (this.fulfilledQueries[objectHash(props)]) {
            return this.fulfilledQueries[objectHash(props)];
        }
    }
    reportPathName(name) {
        this.pathname = name;
    }
}
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 60 * 0.25,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
        },
    },
});
const Prefetch = ({ provider, children }) => {
    const [firstRun, setFirstRun] = React.useState(true);
    if (firstRun) {
        Object.entries(provider.fulfilledQueries).forEach(([hash, data]) => {
            const queryArgs = provider.queries[hash];
            queryClient.setQueryData([JSON.stringify(queryArgs)], {
                pageParams: [1],
                pages: [data]
            });
        });
        Object.entries(provider.fulfilledDependents).forEach(([id, data]) => {
            queryClient.setQueryData(['scoped-id', id], data);
        });
    }
    React.useEffect(() => {
        setFirstRun(false);
    }, []);
    const maybe = React.useCallback((props, modelTrack) => {
        return provider.queue(props, modelTrack);
    }, []);
    const value = React.useMemo(() => ({
        maybe,
        provider,
        queryClient
    }), [maybe]);
    return (React.createElement(PrefetchContext.Provider, { value: value },
        React.createElement(QueryClientProvider, { client: queryClient }, children)));
};
const usePrefetch = (props, modelTrack) => {
    const ctx = React.useContext(PrefetchContext);
    if (ctx) {
        return ctx.maybe(props, modelTrack);
    }
    return null;
};
const usePrefetchAttachedIds = (props, modelTrack) => {
    const ctx = React.useContext(PrefetchContext);
    if (ctx) {
        const existingMaybe = ctx.maybe(props, modelTrack);
        if (existingMaybe) {
            return existingMaybe.ids.reduce((acc, val) => {
                const r = ctx.provider.fulfilledDependents[val];
                if (r) {
                    return {
                        ...acc,
                        [val]: [r]
                    };
                }
                return acc;
            }, {});
        }
    }
    return {};
};
const useQueryClient = () => {
    const ctx = React.useContext(PrefetchContext);
    if (ctx) {
        return ctx.queryClient;
    }
    return null;
};
const createPrefetch = () => new PrefetchStore();
export { Prefetch, usePrefetch, usePrefetchAttachedIds, useQueryClient, createPrefetch, queryClient };
//# sourceMappingURL=prefetcher.js.map